// When an appointment starts and ends, and what a therapist is allowed to save.
//
// Pure functions, no React and no MUI, because every one of these rules was wrong in a way
// that only showed up in the finished dialog:
//
//   * Clicking a DAY in the month view handed the form slotInfo.start = midnight and
//     slotInfo.end = midnight, so "New Appointment" opened on 12:00 AM – 12:00 AM: a
//     zero-length session at midnight, which the therapist had to correct twice on every
//     booking. The 9:00 default two lines above it only applied when no slot was clicked
//     at all.
//   * Nothing checked that the end came after the start, so 14:00 – 09:00 saved happily and
//     rendered as a negative block on the calendar.
//   * Nothing checked the appointment was in the future, so a mistyped year booked a session
//     into last March.
//
// Editing is deliberately laxer than creating: an appointment that has already happened must
// stay editable (that is how a therapist fixes a wrong note on it), so the not-in-the-past
// rule applies only to new bookings.

export const DEFAULT_SESSION_MINUTES = 60;
export const DEFAULT_WORKING_HOURS = { start: 9 * 60, end: 18 * 60 };

// The hour a clinic day starts, used when the therapist picked a date but no time.
const DEFAULT_START_HOUR = 9;

const MINUTE = 60 * 1000;

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const clockMinutes = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value instanceof Date && isValidDate(value)) return value.getHours() * 60 + value.getMinutes();
  const match = String(value ?? '').trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60 ? hours * 60 + minutes : null;
};

const daySchedule = (source, date) => {
  if (!source || !date) return null;
  const day = DAY_NAMES[date.getDay()];
  const days = source.selectedDays || source.hours || source.workingHours || source.availability;
  if (!days || typeof days !== 'object') return null;
  const raw = days[day] || days[day[0].toUpperCase() + day.slice(1)] || days[date.getDay()];
  if (!raw) return Object.keys(days).length || source.selectedDaysConfigured === true ? { closed: true } : null;
  const start = clockMinutes(raw.start ?? raw.open ?? raw.from);
  const end = clockMinutes(raw.end ?? raw.close ?? raw.to);
  return start != null && end != null && end > start ? { start, end } : { closed: true };
};

// Resolve the effective opening hours without assuming every existing organization has
// migrated the optional hours fields yet. A configured day wins; otherwise an explicitly
// configured schedule marks that day closed; no schedule falls back to 09:00–18:00.
export function workingHoursFor(date, { location, doctor } = {}) {
  const configured = daySchedule(doctor, date) || daySchedule(location, date);
  return configured || DEFAULT_WORKING_HOURS;
}

export function overlapsExisting(start, end, appointments = [], doctorId, currentId) {
  return appointments.some((appointment) => {
    if (!appointment || appointment.id === currentId || appointment.appointment_status === 'cancelled') return false;
    const appointmentDoctor = appointment.doctor_id ?? appointment.primaryDoctorId;
    if (doctorId && appointmentDoctor && String(appointmentDoctor) !== String(doctorId)) return false;
    const existingStart = new Date(appointment.start || appointment.appointment_starttime);
    const existingEnd = new Date(appointment.end || appointment.appointment_endtime);
    return isValidDate(existingStart) && isValidDate(existingEnd) && start < existingEnd && end > existingStart;
  });
}

const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());

const isMidnight = (date) =>
  date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0;

export const addMinutes = (date, minutes) => new Date(date.getTime() + minutes * MINUTE);

// Combine the date picker's day with the time picker's clock time. The two fields are
// separate in the form but one instant in the database, and taking the day from the wrong
// one is how an appointment ends up on yesterday at the right time.
export function combine(day, time) {
  if (!isValidDate(day) || !isValidDate(time)) return null;
  const out = new Date(day);
  out.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return out;
}

// What the dialog should open with for a click on the calendar.
//
// A month-cell click arrives as midnight-to-midnight (or a span of whole days); that is the
// calendar saying "this day", not "this time", so it gets the clinic's default hour. A click
// on a real time slot in the week/day view is the therapist saying exactly when, and is kept
// — only its length is repaired if the view handed us a zero-length slot.
export function initialSlot(slotInfo, now = new Date()) {
  const fallbackDay = isValidDate(slotInfo?.start) ? new Date(slotInfo.start) : new Date(now);
  const start = new Date(fallbackDay);

  const pickedATime =
    isValidDate(slotInfo?.start) &&
    !(isMidnight(slotInfo.start) && (!isValidDate(slotInfo.end) || slotInfo.end - slotInfo.start >= 24 * 60 * MINUTE || isMidnight(slotInfo.end)));

  if (!pickedATime) {
    start.setHours(DEFAULT_START_HOUR, 0, 0, 0);
  } else {
    start.setSeconds(0, 0);
  }

  const slotLength =
    pickedATime && isValidDate(slotInfo.end) ? (slotInfo.end - slotInfo.start) / MINUTE : 0;
  const end = addMinutes(start, slotLength > 0 ? slotLength : DEFAULT_SESSION_MINUTES);

  return { date: new Date(start), start, end };
}

// Moving the start moves the end with it, keeping the length the therapist already has. The
// alternative — leaving the end where it was — silently produces a negative session the
// moment they drag the start past it, and they have to fix two fields to change one thing.
export function startChanged(form, nextStart) {
  if (!isValidDate(nextStart)) return { appointment_starttime: nextStart };
  const previousStart = form?.appointment_starttime;
  const previousEnd = form?.appointment_endtime;
  const length =
    isValidDate(previousStart) && isValidDate(previousEnd) && previousEnd > previousStart
      ? (previousEnd - previousStart) / MINUTE
      : DEFAULT_SESSION_MINUTES;
  return { appointment_starttime: nextStart, appointment_endtime: addMinutes(nextStart, length) };
}

// The rules, in the order a therapist would hit them. Returns null when the times are fine,
// otherwise { field, message } so the dialog can point at the offending picker.
export function validateTimes(form, { isEditing = false, now = new Date(), location, doctor, appointments = [], serverChecksWorkingHours = false } = {}) {
  const { appointment_date: day, appointment_starttime: start, appointment_endtime: end } = form || {};

  if (!isValidDate(day)) return { field: 'appointment_date', message: 'Pick a date for the appointment.' };
  if (!isValidDate(start)) return { field: 'appointment_starttime', message: 'Pick a start time.' };
  if (!isValidDate(end)) return { field: 'appointment_endtime', message: 'Pick an end time.' };

  const startsAt = combine(day, start);
  const endsAt = combine(day, end);

  if (endsAt <= startsAt) {
    return {
      field: 'appointment_endtime',
      message: 'The end time must be after the start time.',
    };
  }

  // Only for new bookings: a past appointment has to stay editable, or a therapist can never
  // correct the record of a session that already happened.
  if (!isEditing && startsAt < now) {
    return {
      field: 'appointment_starttime',
      message: 'That time has already passed. Pick a date and time in the future.',
    };
  }

  if (!isEditing && !serverChecksWorkingHours) {
    const hours = workingHoursFor(startsAt, { location, doctor });
    const startMinutes = startsAt.getHours() * 60 + startsAt.getMinutes();
    const endMinutes = endsAt.getHours() * 60 + endsAt.getMinutes();
    if (hours.closed || startMinutes < hours.start || endMinutes > hours.end) {
      const format = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
      return { field: 'appointment_starttime', message: `Choose a time within the clinic hours (${hours.closed ? 'closed on this day' : `${format(hours.start)}–${format(hours.end)}`}).` };
    }
  }

  if (!isEditing && overlapsExisting(startsAt, endsAt, appointments, form.doctor_id, form.id)) {
    return { field: 'appointment_starttime', message: 'This time overlaps an existing appointment for the selected therapist.' };
  }

  return null;
}
